import { useState, type FormEvent } from 'react'
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { signIn, useSession } from '@/lib/auth-client'
import { getLocalizedPath } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'
import { AuthCard, AuthField, AuthNotice, authButtonClass, authLinkClass } from '@/pages/auth-ui'
import { ForgotPasswordForm, ResetPasswordForm } from '@/pages/PasswordReset'

const ERROR_KEYS: Record<number, string> = {
  401: 'auth.errors.invalidCredentials',
  403: 'auth.errors.disabled',
  429: 'auth.errors.tooManyAttempts',
}

export function Login() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: session } = useSession()
  // Il link dell'email passa da /api/auth/reset-password/:token e torna qui con ?token= (o ?error= se scaduto);
  // i link d'invito (area riservata → Utenti → Invita) aggiungono ?invite=1.
  const [searchParams, setSearchParams] = useSearchParams()
  const token = searchParams.get('token')
  const linkError = searchParams.get('error')
  const isInvite = searchParams.get('invite') === '1'
  const [isForgotOpen, setIsForgotOpen] = useState(false)
  // Password appena scelta dal link: 'reset' (password dimenticata) o 'invite' (account attivato).
  const [passwordSet, setPasswordSet] = useState<'reset' | 'invite' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const target =
    (location.state as { from?: string } | null)?.from ??
    getLocalizedPath('admin', i18n.language as SupportedLanguage)

  if (session?.user.enabled) return <Navigate to={target} replace />

  function backToLogin(done: typeof passwordSet = null) {
    setSearchParams({}, { replace: true })
    setIsForgotOpen(false)
    setPasswordSet(done)
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setError(null)
    setPasswordSet(null)
    setIsSubmitting(true)
    const { error: signInError } = await signIn.email({
      email: String(form.get('email')),
      password: String(form.get('password')),
    })
    setIsSubmitting(false)
    if (signInError) {
      setError(t(ERROR_KEYS[signInError.status] ?? 'auth.errors.generic'))
      return
    }
    navigate(target, { replace: true })
  }

  return (
    <main className="relative isolate flex min-h-screen items-center justify-center overflow-hidden px-4 pb-8 pt-24 md:pt-28">
      {/* Stessa griglia sfumata della home. */}
      <div aria-hidden="true" className="chart-grid absolute inset-0 -z-10 opacity-40 [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]" />
      {token ? (
        <ResetPasswordForm
          token={token}
          invite={isInvite}
          onBack={() => backToLogin()}
          onDone={() => backToLogin(isInvite ? 'invite' : 'reset')}
        />
      ) : isForgotOpen || linkError ? (
        <ForgotPasswordForm expired={Boolean(linkError)} onBack={() => backToLogin()} />
      ) : (
        <AuthCard headingId="login-heading" title={t('auth.login.title')} onSubmit={handleSubmit}>
          {(passwordSet || error) && (
            <div className="mb-5">
              {error ? (
                <AuthNotice tone="error">{error}</AuthNotice>
              ) : (
                <AuthNotice tone="success" title={t(`auth.${passwordSet}.doneTitle`)}>
                  {t(`auth.${passwordSet}.done`)}
                </AuthNotice>
              )}
            </div>
          )}
          <div className="space-y-4">
            <AuthField
              label={t('auth.fields.email')}
              name="email"
              type="email"
              autoComplete="email"
              autoFocus={passwordSet !== null}
              required
            />
            <AuthField
              label={t('auth.fields.password')}
              name="password"
              type="password"
              autoComplete="current-password"
              required
              labelAction={
                <button type="button" onClick={() => setIsForgotOpen(true)} className={authLinkClass}>
                  {t('auth.login.forgotPassword')}
                </button>
              }
            />
          </div>
          <button type="submit" disabled={isSubmitting} className={`mt-6 w-full ${authButtonClass}`}>
            {isSubmitting ? t('auth.login.submitting') : t('auth.login.submit')}
          </button>
        </AuthCard>
      )}
    </main>
  )
}
